export const TELEMETRY_CENSOR = '[Redacted]';

const FIELD_NAMES = [
  'cookie',
  'cookies',
  'authorization',
  'code',
  'email',
  'password',
  'sessionId',
  'session_id',
  'sessionToken',
  'token',
  'refresh_token',
  'idToken',
  'id_token',
  'access_token',
  'accessToken',
  'sub',
  '_cars_web_key',
  'rv_session',
  'set-cookie',
  'setCookie',
] as const;

const SENSITIVE_KEYS = new Set(FIELD_NAMES.map((name) => name.toLowerCase()));

const SECRET_KEY =
  'code|email|password|sessionId|session_id|sessionToken|token|idToken|id_token|access_token|accessToken|refresh_token|sub|authorization|_cars_web_key|rv_session|CARS_[A-Za-z0-9_]+|X-EDU-Session-[A-Za-z0-9._-]+';

const DIAGNOSTIC_PARENTS = new Set(['status', 'err', 'error']);

const COOKIE_ATTRIBUTES = new Set([
  'path',
  'domain',
  'expires',
  'max-age',
  'samesite',
  'secure',
  'httponly',
  'partitioned',
]);

function isSensitiveTelemetryKey(key: string): boolean {
  const normalized = key.toLowerCase();
  if (SENSITIVE_KEYS.has(normalized)) {
    return true;
  }
  return (
    normalized.startsWith('cars_') || normalized.startsWith('x-edu-session-')
  );
}

function isErrnoString(value: unknown): boolean {
  return typeof value === 'string' && /^E[A-Z0-9][A-Z0-9_]*$/.test(value);
}

function isHttpStatus(value: unknown): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 100 &&
    value <= 599
  );
}

function isDiagnosticContext(
  parentKey: string | undefined,
  record: object,
): boolean {
  if (
    parentKey !== undefined &&
    DIAGNOSTIC_PARENTS.has(parentKey.toLowerCase())
  ) {
    return true;
  }
  return 'errno' in record || 'syscall' in record;
}

function keepCode(
  key: string,
  child: unknown,
  parentKey: string | undefined,
  record: object,
): boolean {
  return (
    key.toLowerCase() === 'code' &&
    isDiagnosticContext(parentKey, record) &&
    (isErrnoString(child) || isHttpStatus(child))
  );
}

function censorCookieAssignments(body: string): string {
  return body.replace(
    /([^=;\s,"']{1,256})=("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^;\s,"']*)/g,
    (match, name: string, value: string) => {
      if (COOKIE_ATTRIBUTES.has(name.toLowerCase())) {
        return match;
      }
      if (value.startsWith('"')) {
        return `${name}="${TELEMETRY_CENSOR}"`;
      }
      if (value.startsWith("'")) {
        return `${name}='${TELEMETRY_CENSOR}'`;
      }
      return `${name}=${TELEMETRY_CENSOR}`;
    },
  );
}

function readQuoted(input: string, start: number): number {
  const quote = input[start];
  let index = start + 1;
  while (index < input.length) {
    if (input[index] === '\\') {
      index += 2;
      continue;
    }
    if (input[index] === quote) {
      return index + 1;
    }
    index += 1;
  }
  return input.length;
}

function readBracketed(input: string, start: number): number {
  let depth = 0;
  let index = start;
  while (index < input.length) {
    const char = input[index];
    if (char === '"' || char === "'") {
      index = readQuoted(input, index);
      continue;
    }
    if (char === '[') {
      depth += 1;
    } else if (char === ']') {
      depth -= 1;
      index += 1;
      if (depth === 0) {
        return index;
      }
      continue;
    }
    index += 1;
  }
  return input.length;
}

function endOfCookieHeaderValue(input: string, start: number): number {
  if (start >= input.length) {
    return start;
  }
  const first = input[start];
  if (first === '"' || first === "'") {
    return readQuoted(input, start);
  }
  if (first === '[') {
    return readBracketed(input, start);
  }
  const newline = input.indexOf('\n', start);
  return newline === -1 ? input.length : newline;
}

function censorCookieHeaders(input: string): string {
  const pattern =
    /(?:^|[{,\s])(['"]?)(set-cookie|setcookie|cookie)\1(?=\s*[:=])/gi;
  let result = '';
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(input)) !== null) {
    let index = match.index + match[0].length;
    while (index < input.length && /\s/.test(input[index] ?? '')) {
      index += 1;
    }
    const separator = input[index];
    if (separator !== ':' && separator !== '=') {
      continue;
    }
    index += 1;
    while (index < input.length && /\s/.test(input[index] ?? '')) {
      index += 1;
    }
    const valueEnd = endOfCookieHeaderValue(input, index);
    result +=
      input.slice(cursor, index) +
      censorCookieAssignments(input.slice(index, valueEnd));
    cursor = valueEnd;
    if (valueEnd <= match.index) {
      pattern.lastIndex = match.index + 1;
      continue;
    }
    pattern.lastIndex = valueEnd;
  }
  return result + input.slice(cursor);
}

function censorSecretFields(input: string): string {
  const quoted = (quote: '"' | "'"): RegExp =>
    new RegExp(
      `((?:^|[{,\\s])(["']?)(?:${SECRET_KEY})\\2\\s*:\\s*${quote})((?:\\\\.|[^${quote}\\\\])*)(${quote})`,
      'gi',
    );
  return input
    .replace(
      quoted('"'),
      (
        _match,
        prefix: string,
        _keyQuote: string,
        _value: string,
        end: string,
      ) => `${prefix}${TELEMETRY_CENSOR}${end}`,
    )
    .replace(
      quoted("'"),
      (
        _match,
        prefix: string,
        _keyQuote: string,
        _value: string,
        end: string,
      ) => `${prefix}${TELEMETRY_CENSOR}${end}`,
    )
    .replace(
      new RegExp(
        `((?:^|[{,\\s])(["']?)(?:${SECRET_KEY})\\2\\s*:\\s*)(?!["'\\[]|Bearer\\b|Basic\\b)([^\\s,}'"]+)`,
        'gi',
      ),
      `$1${TELEMETRY_CENSOR}`,
    )
    .replace(
      new RegExp(
        `((?:^|[?&\\s{,])(?:${SECRET_KEY})\\s*=\\s*)(?:"(?:\\\\.|[^"\\\\])*"|'(?:\\\\.|[^'\\\\])*'|[^\\s"'&,;#]*)`,
        'gi',
      ),
      `$1${TELEMETRY_CENSOR}`,
    );
}

export function censorString(input: string): string {
  return censorCookieHeaders(
    censorSecretFields(
      input
        .replace(/\b(Bearer|Basic)\s+[^\s"',;&]+/gi, `$1 ${TELEMETRY_CENSOR}`)
        .replace(
          /[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,24}/g,
          TELEMETRY_CENSOR,
        ),
    ),
  );
}

export function censorError(
  error: Error,
  seen: WeakMap<object, unknown> = new WeakMap(),
): Error {
  const cached = seen.get(error);
  if (cached instanceof Error) {
    return cached;
  }
  const censored = new Error(censorString(error.message));
  censored.name = error.name;
  seen.set(error, censored);
  if (typeof error.stack === 'string') {
    censored.stack = censorString(error.stack);
  }
  const code = (error as { code?: unknown }).code;
  if (isErrnoString(code) || isHttpStatus(code)) {
    (censored as { code?: unknown }).code = code;
  }
  if (error.cause !== undefined && error.cause !== error) {
    censored.cause = censorLoggedValue(error.cause, seen);
  }
  return censored;
}

export function censorLoggedValue(
  value: unknown,
  seen: WeakMap<object, unknown> = new WeakMap(),
  parentKey?: string,
): unknown {
  if (typeof value === 'string') {
    return censorString(value);
  }
  if (typeof value !== 'object' || value === null) {
    return value;
  }
  const cached = seen.get(value);
  if (cached !== undefined) {
    return cached;
  }
  if (value instanceof Error) {
    return censorError(value, seen);
  }
  if (value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    const output: unknown[] = [];
    seen.set(value, output);
    for (const item of value) {
      output.push(censorLoggedValue(item, seen));
    }
    return output;
  }

  const output: Record<string, unknown> = {};
  seen.set(value, output);
  for (const [key, child] of Object.entries(value)) {
    if (
      isSensitiveTelemetryKey(key) &&
      !keepCode(key, child, parentKey, value)
    ) {
      output[key] = TELEMETRY_CENSOR;
      continue;
    }
    output[key] = censorLoggedValue(child, seen, key);
  }
  return output;
}
